import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  CogIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  PhotoIcon,
  QrCodeIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  TagIcon,
  BoltIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const EquipmentList = ({ equipment = [] }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  // Update equipment status mutation
  const updateStatusMutation = useMutation(
    ({ id, status }) => axios.patch(`/equipments/${id}/status`, { status }),
    {
      onSuccess: () => {
        toast.success('Statut mis à jour avec succès!');
        queryClient.invalidateQueries('equipment');
      },
      onError: () => {
        toast.error('Erreur lors de la mise à jour du statut');
      },
    }
  );

  // Delete equipment mutation
  const deleteEquipmentMutation = useMutation(
    (equipmentId) => axios.delete(`/equipments/${equipmentId}`),
    {
      onSuccess: () => {
        toast.success('Équipement supprimé avec succès!');
        queryClient.invalidateQueries('equipment');
      },
      onError: () => {
        toast.error('Erreur lors de la suppression');
      },
    }
  );

  const equipmentCategories = [
    { id: 'cardio', name: 'Cardio', icon: BoltIcon, color: 'bg-red-500' },
    { id: 'strength', name: 'Musculation', icon: CogIcon, color: 'bg-blue-500' },
    { id: 'functional', name: 'Fonctionnel', icon: ShieldCheckIcon, color: 'bg-green-500' },
    { id: 'accessories', name: 'Accessoires', icon: WrenchScrewdriverIcon, color: 'bg-purple-500' },
  ];

  // Filter and sort equipment
  const filteredEquipment = equipment
    .filter(item => {
      const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      
      return matchesSearch && matchesStatus && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        case 'purchase-date':
          return new Date(b.purchaseDate || 0) - new Date(a.purchaseDate || 0);
        case 'last-maintenance':
          return new Date(b.lastMaintenance || 0) - new Date(a.lastMaintenance || 0);
        default:
          return 0;
      }
    });

  const handleStatusUpdate = (equipmentId, newStatus) => {
    updateStatusMutation.mutate({ id: equipmentId, status: newStatus });
  };

  const handleDeleteEquipment = (equipmentId, equipmentName) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'équipement "${equipmentName}" ?`)) {
      deleteEquipmentMutation.mutate(equipmentId);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'maintenance':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'out-of-order':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'reserved':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusText = (status) => {
    const statusTexts = {
      available: 'Disponible',
      maintenance: 'Maintenance',
      'out-of-order': 'Hors service',
      reserved: 'Réservé',
    };
    return statusTexts[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return CheckCircleIcon;
      case 'maintenance':
        return WrenchScrewdriverIcon;
      case 'out-of-order':
        return ExclamationTriangleIcon;
      case 'reserved':
        return ClockIcon;
      default:
        return CogIcon;
    }
  };

  const getMaintenanceStatus = (equipment) => {
    if (!equipment.lastMaintenance) {
      return { status: 'never', text: 'Jamais entretenu', color: 'text-red-400' };
    }
    
    const daysSinceMaintenance = Math.floor((new Date() - new Date(equipment.lastMaintenance)) / (1000 * 60 * 60 * 24));
    
    if (daysSinceMaintenance > 90) {
      return { status: 'overdue', text: `${daysSinceMaintenance} jours`, color: 'text-red-400' };
    } else if (daysSinceMaintenance > 60) {
      return { status: 'soon', text: `${daysSinceMaintenance} jours`, color: 'text-yellow-400' };
    } else {
      return { status: 'recent', text: `${daysSinceMaintenance} jours`, color: 'text-green-400' };
    }
  };

  // Calculate summary stats
  const stats = {
    total: filteredEquipment.length,
    available: filteredEquipment.filter(e => e.status === 'available').length,
    maintenance: filteredEquipment.filter(e => e.status === 'maintenance').length,
    outOfOrder: filteredEquipment.filter(e => e.status === 'out-of-order').length,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Total</p>
              <p className="text-xl font-bold text-white">{stats.total}</p>
            </div>
            <CogIcon className="h-5 w-5 text-blue-400" />
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Disponibles</p>
              <p className="text-xl font-bold text-white">{stats.available}</p>
            </div>
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Maintenance</p>
              <p className="text-xl font-bold text-white">{stats.maintenance}</p>
            </div>
            <WrenchScrewdriverIcon className="h-5 w-5 text-yellow-400" />
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Hors service</p>
              <p className="text-xl font-bold text-white">{stats.outOfOrder}</p>
            </div>
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
            <input
              type="text"
              placeholder="Rechercher équipement..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="available">Disponible</option>
            <option value="maintenance">Maintenance</option>
            <option value="out-of-order">Hors service</option>
            <option value="reserved">Réservé</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Toutes catégories</option>
            <option value="cardio">Cardio</option>
            <option value="strength">Musculation</option>
            <option value="functional">Fonctionnel</option>
            <option value="accessories">Accessoires</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="name">Nom</option>
            <option value="category">Catégorie</option>
            <option value="status">Statut</option>
            <option value="purchase-date">Date d'achat</option>
            <option value="last-maintenance">Dernière maintenance</option>
          </select>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white hover:bg-white/20 transition-colors"
            >
              {viewMode === 'grid' ? 'Liste' : 'Grille'}
            </button>
          </div>
        </div>
      </div>

      {/* Equipment Display */}
      {filteredEquipment.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEquipment.map((item, index) => {
              const category = equipmentCategories.find(c => c.id === item.category);
              const StatusIcon = getStatusIcon(item.status);
              const maintenanceStatus = getMaintenanceStatus(item);
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
                >
                  {/* Equipment Image */}
                  <div className="relative mb-4">
                    {item.photo ? (
                      <img
                        src={item.photo}
                        alt={item.name}
                        className="w-full h-48 object-cover rounded-xl"
                      />
                    ) : (
                      <div className="w-full h-48 bg-white/5 rounded-xl flex items-center justify-center">
                        <PhotoIcon className="h-12 w-12 text-white/30" />
                      </div>
                    )}
                    <div className="absolute top-3 right-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(item.status)}`}>
                        {getStatusText(item.status)}
                      </span>
                    </div>
                  </div>

                  {/* Equipment Info */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${category?.color || 'bg-gray-500'}`}>
                        {category?.icon && React.createElement(category.icon, { className: "h-5 w-5 text-white" })}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white">{item.name}</h3>
                        <p className="text-white/60 text-sm">{item.brand} - {item.model}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-white/50">Numéro de série</p>
                        <p className="text-white font-medium">{item.serialNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-white/50">Catégorie</p>
                        <p className="text-white font-medium">{category?.name || 'Non défini'}</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/50">Dernière maintenance:</span>
                        <span className={maintenanceStatus.color}>
                          {maintenanceStatus.text}
                        </span>
                      </div>
                      {item.purchaseDate && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-white/50">Acheté le:</span>
                          <span className="text-white">
                            {new Date(item.purchaseDate).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      )}
                      {item.warranty && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-white/50">Garantie:</span>
                          <span className="text-white">
                            {new Date(item.warranty) > new Date() ? 'Valide' : 'Expirée'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 pt-3 border-t border-white/10">
                      <Link
                        to={`/equipment/${item.id}/edit`}
                        className="flex-1 bg-blue-500/20 text-blue-400 py-2 px-3 rounded-xl hover:bg-blue-500/30 transition-colors text-center text-sm font-medium"
                      >
                        Modifier
                      </Link>
                      
                      <div className="flex items-center space-x-1">
                        {item.status === 'available' && (
                          <button
                            onClick={() => handleStatusUpdate(item.id, 'maintenance')}
                            className="p-2 bg-yellow-500/20 text-yellow-400 rounded-xl hover:bg-yellow-500/30 transition-colors"
                            title="Mettre en maintenance"
                          >
                            <WrenchScrewdriverIcon className="h-4 w-4" />
                          </button>
                        )}
                        
                        {item.status === 'maintenance' && (
                          <button
                            onClick={() => handleStatusUpdate(item.id, 'available')}
                            className="p-2 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-colors"
                            title="Marquer comme disponible"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleDeleteEquipment(item.id, item.name)}
                          className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          // List View
          <div className="space-y-4">
            {filteredEquipment.map((item, index) => {
              const category = equipmentCategories.find(c => c.id === item.category);
              const maintenanceStatus = getMaintenanceStatus(item);
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${category?.color || 'bg-gray-500'}`}>
                        {category?.icon && React.createElement(category.icon, { className: "h-6 w-6 text-white" })}
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-semibold text-white">{item.name}</h3>
                        <p className="text-white/60 text-sm">{item.brand} - {item.model}</p>
                        <div className="flex items-center space-x-4 mt-1 text-white/50 text-sm">
                          <span>S/N: {item.serialNumber || 'N/A'}</span>
                          <span>Catégorie: {category?.name}</span>
                          <span className={maintenanceStatus.color}>
                            Maintenance: {maintenanceStatus.text}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(item.status)}`}>
                        {getStatusText(item.status)}
                      </span>
                      
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/equipment/${item.id}/edit`}
                          className="p-2 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-colors"
                          title="Modifier"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </Link>
                        
                        {item.status === 'available' && (
                          <button
                            onClick={() => handleStatusUpdate(item.id, 'maintenance')}
                            className="p-2 bg-yellow-500/20 text-yellow-400 rounded-xl hover:bg-yellow-500/30 transition-colors"
                            title="Mettre en maintenance"
                          >
                            <WrenchScrewdriverIcon className="h-5 w-5" />
                          </button>
                        )}
                        
                        {item.status === 'maintenance' && (
                          <button
                            onClick={() => handleStatusUpdate(item.id, 'available')}
                            className="p-2 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-colors"
                            title="Marquer comme disponible"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleDeleteEquipment(item.id, item.name)}
                          className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )
      ) : (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 text-center">
          <CogIcon className="h-16 w-16 text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Aucun équipement trouvé
          </h3>
          <p className="text-white/60 mb-6">
            {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
              ? 'Aucun équipement ne correspond à vos critères de recherche.'
              : 'Aucun équipement enregistré pour le moment.'}
          </p>
          <Link
            to="/equipment/new"
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 inline-block"
          >
            Ajouter un Équipement
          </Link>
        </div>
      )}
    </motion.div>
  );
};

export default EquipmentList; 